<script setup>
import InputVue from './InputCompVue'
import TagVue from './TagVue'

import { ref, useTemplateRef } from 'vue'

const { modelValue, tagList } = defineProps({
  name: String,
  label: String,
  cssClasses: String,
  labelAsPlaceholder: Boolean,
  placeholder: String,
  modelValue: {
    type: String,
    default: '',
  },
  alignment: {
    type: String,
    default: 'horizontal',
  },
  tagList: {
    type: Array,
    default: [],
  },
})
const emit = defineEmits([
  'update:modelValue',
])
const hasError = ref(false)
const tags = ref([])
const withTagList = ref(tagList.length > 0)
const tagsCont = useTemplateRef('tagsCont')
const inputEl = useTemplateRef('inputEl')
const matchedTags = ref([])
const showSuggestions = ref(false)
const highlighted = ref(-1)

const enterTag = (e, tag) => {
  inputEl.value.value = ''
  showSuggestions.value = false
  if (tag !== undefined) {
    tags.value.push(tag)
    emit('update:modelValue', tags)
  } else if (e.target.value.length > 0) {
    tags.value.push(e.target.value)
    emit('update:modelValue', tags)
  }
  // the timing of this call is too early, need to wait until after tag render
  // adjustMargin()
}

const adjustMargin = () => {
  const dims = tagsCont.value.getClientRects()
  inputEl.value.style.paddingLeft= `${dims.width}px`
}

const onInput = (e) => {
  const i = e.target.value
  matchedTags.value = tagList.filter(t => t.toLowerCase().includes(i.toLowerCase()))
  showSuggestions.value = matchedTags.value.length > 0 && i.length > 0
}

const reset = () => {
  tags.value = []
}

const getValue = () => {
  return tags.value
}

defineExpose({ reset, getValue })

const onRemoveTag = (tag) => {
  const idx = tags.value.indexOf(tag)
  const t = tags.value.splice(idx, 1)
}

const onTabSuggestion = (e) => {
  if (e.shiftKey) highlighted.value -= 1
  else highlighted.value += 1

  if (highlighted.value < 0 || highlighted.value >= matchedTags.value.length) {
    highlighted.value = -1
    focusInput()
  }
}

const selectSuggestion = (e, i) => {
  if (matchedTags.value.length > 0) {
    let tag
    if (typeof i === 'number') tag = matchedTags.value[i]
    else tag = matchedTags.value[highlighted.value]
    enterTag(e, tag)
  }
}

const focusInput = () => {
  inputEl.value.focus()
}

</script>

<template>
  <div class="tag-vue-wrapper">
    <InputVue
      :name="name"
      :label="label"
      class="tag-input-cont"
      :cssClasses="cssClasses"
      :labelAsPlaceholder="labelAsPlaceholder"
      :hasError="hasError"
      :alignment="alignment"
      @keyup.enter="enterTag"
      @input="onInput">
      <template #input-slot>
        <div class="tag-input-wrapper">
          <input 
            ref="inputEl"
            class="form-control" 
            autocomplete="off"
            :name="name" 
            type="text"
            :placeholder="labelAsPlaceholder ? label : placeholder " />

          <div class="tags-cont flex" ref="tagsCont">
            <TagVue v-for="(t,i) in tags" :key="i" :tagLabel="t" @tag:remove="onRemoveTag" />
          </div>
        </div>
      </template>
    </InputVue>

    <ul v-if="withTagList" class="tag-list" v-show="showSuggestions">
      <li v-for="(t,i) in matchedTags"
        :key="i"
        :tabindex="0"
        :data-index="i"
        :data-last="i === matchedTags.length - 1"
        :data-suggestion="t"
        :class="{highlighted:highlighted === i}"
        class="tag-suggestion"
        @click="selectSuggestion($event, i)"
        @keyup.enter="selectSuggestion($event, i)"
        @keyup.tab.prevent="onTabSuggestion($event)">
        {{t}}
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.tag-vue-wrapper {
  position: relative;

  .tag-input-cont {
    display: flex;

    .tag-input-wrapper {
      position: relative;
      
      .tags-cont {
        top: 0;
        margin: 5px 0;
      }
    }
  }

  .tag-list {
    width: 100%;
    z-index: 10;
    border: 1px solid color(primary);
    background-color: color(surface-bright);
    position: absolute;
    padding: 0 $paddingSmall;
    text-align: left;
    margin: 0;

    .tag-suggestion {
      list-style: none;
      color: color(secondary);
      padding: $paddingXSmall;
      text-align: left;

      &.highlighted {
        background-color: color(surface-variant);
      }

      &:focus {
        //background-color: color(surface-variant);
        outline: none;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }
}

</style>
